/**
 * ProfileOrders Component
 *
 * Displays user's orders based on their current role (client or worker)
 *
 * Client Mode:
 * - PUBLIC orders: show responses button with unread badge
 * - DIRECT orders: show assigned worker info
 * - Delete order functionality
 *
 * Worker Mode:
 * - Shows orders where user is assigned as worker
 * - Displays order details and client information
 */

'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Spinner } from '@/src/shared/ui/spinner/Spinner';
import { useModal } from '@/src/shared/hooks/useModal';
import { ordersApi, type OrderResponse, type OrderStatus } from '@/src/shared/api/orders.api';
import { proposalsApi, type ProposalResponse } from '@/src/shared/api/proposals.api';
import { OrderCard } from '@/src/features/profile/components/my-client/OrderCard';
import { WorkerOrderCard } from '@/src/features/profile/components/my-worker/WorkerOrderCard';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { SortSelect, type SortOption } from '@/src/features/profile/components/shared/SortSelect';

export interface ProfileOrdersProps {
  className?: string;
  /**
   * Role to fetch orders for
   * If not provided, will be determined from activeActor
   */
  role?: 'client' | 'worker';
  /**
   * Callback when order card is clicked (for client orders to view proposals)
   */
  onOrderClick?: (orderId: string) => void;
}

// Order with proposals metadata
interface OrderWithProposalsMetadata extends OrderResponse {
  proposalsCount: number;
  unreadProposalsCount: number;
  proposalsLoading: boolean;
}

export function ProfileOrders({ className, role: propRole, onOrderClick }: ProfileOrdersProps) {
  const t = useTranslations('profile.orders');
  const { open: openDeleteModal } = useModal('delete-order');
  const { open: openCompleteModal } = useModal('complete-order');
  const { activeActor } = useAuthStore();

  // Determine role: use prop if provided, otherwise derive from activeActor
  const role = propRole || (activeActor?.type === 'WORKER' ? 'worker' : 'client');

  const [orders, setOrders] = useState<OrderWithProposalsMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrderForDelete, setSelectedOrderForDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // Sort and filter state
  const [sortBy, setSortBy] = useState<'updatedAt' | 'createdAt' | 'budget' | 'title'>('updatedAt');
  const [filterStatus, setFilterStatus] = useState<OrderStatus | 'all'>('all');

  // Load orders and their proposals metadata
  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const myOrders = await ordersApi.getMyOrders(role);

      // Initialize orders with empty proposals metadata
      const ordersWithMetadata: OrderWithProposalsMetadata[] = myOrders.map((order) => ({
        ...order,
        proposalsCount: 0,
        unreadProposalsCount: 0,
        proposalsLoading: order.type === 'PUBLIC' && order.status === 'PUBLISHED',
      }));

      setOrders(ordersWithMetadata);

      // Load proposals count for published PUBLIC orders
      for (const order of ordersWithMetadata) {
        if (order.type === 'PUBLIC' && order.status === 'PUBLISHED') {
          try {
            const proposals = await proposalsApi.getOrderProposals(order.id);
            const pendingProposals = proposals.filter((p) => p.status === 'PENDING');

            setOrders((prev) =>
              prev.map((o) =>
                o.id === order.id
                  ? {
                      ...o,
                      proposalsCount: proposals.length,
                      unreadProposalsCount: pendingProposals.length,
                      proposalsLoading: false,
                    }
                  : o
              )
            );
          } catch {
            setOrders((prev) =>
              prev.map((o) =>
                o.id === order.id ? { ...o, proposalsLoading: false } : o
              )
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [role, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleCancelOrder = async (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    try {
      await ordersApi.updateStatus(orderId, 'CANCELLED');
      toast.success(t('cancelSuccess') || 'Order cancelled');
      loadOrders();
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error(t('cancelError') || 'Failed to cancel order');
    }
  };

  const handleDeleteOrder = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      setSelectedOrderForDelete({ id: order.id, title: order.title });
      openDeleteModal({
        orderId: order.id,
        orderTitle: order.title,
        onSuccess: () => {
          loadOrders();
          setSelectedOrderForDelete(null);
        },
      });
    }
  };

  const handleCompleteOrder = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      openCompleteModal({
        orderId: order.id,
        orderTitle: order.title,
        workerName: order.workerActor?.displayName || order.workerActor?.name,
        agreedPrice: order.agreedPrice,
        onSuccess: () => {
          loadOrders();
        },
      });
    }
  };

  const handleAcceptDirectOrder = async (orderId: string) => {
    try {
      await ordersApi.acceptDirectOrder(orderId);
      toast.success(t('acceptSuccess'));
      loadOrders();
    } catch (error) {
      console.error('Failed to accept order:', error);
      toast.error(t('acceptError'));
    }
  };

  const handleRenewOrder = async (orderId: string) => {
    try {
      await ordersApi.renew(orderId);
      toast.success(t('renewSuccess') || 'Order renewed successfully');
      loadOrders();
    } catch (error) {
      console.error('Failed to renew order:', error);
      toast.error(t('renewError') || 'Failed to renew order');
    }
  };

  const handleRestoreOrder = async (orderId: string) => {
    try {
      await ordersApi.restore(orderId);
      toast.success(t('restoreSuccess') || 'Order restored successfully');
      loadOrders();
    } catch (error) {
      console.error('Failed to restore order:', error);
      toast.error(t('restoreError') || 'Failed to restore order');
    }
  };

  // Worker marks order as complete (IN_PROGRESS -> PENDING_REVIEW)
  const handleWorkerComplete = async (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;

    try {
      await ordersApi.updateStatus(orderId, 'PENDING_REVIEW');
      toast.success(t('markedComplete'));
      loadOrders();
    } catch (error) {
      console.error('Failed to mark order as complete:', error);
      toast.error(t('markCompleteError'));
    }
  };

  // Sort options
  const sortOptions: SortOption[] = useMemo(
    () => [
      { value: 'updatedAt', label: t('sort.updatedAt') },
      { value: 'createdAt', label: t('sort.createdAt') },
      { value: 'budget', label: t('sort.budget') },
      { value: 'title', label: t('sort.title') },
    ],
    [t]
  );

  // Filter options
  const filterOptions: SortOption[] = useMemo(
    () => [
      { value: 'all', label: t('filter.all') },
      { value: 'DRAFT', label: t('filter.draft') },
      { value: 'PUBLISHED', label: t('filter.published') },
      { value: 'IN_PROGRESS', label: t('filter.in_progress') },
      { value: 'PENDING_REVIEW', label: t('filter.pending_review') },
      { value: 'COMPLETED', label: t('filter.completed') },
      { value: 'CANCELLED', label: t('filter.cancelled') },
      { value: 'DISPUTED', label: t('filter.disputed') },
    ],
    [t]
  );

  // Filtered and sorted orders
  const filteredAndSortedOrders = useMemo(() => {
    let result = [...orders];

    // Apply filter
    if (filterStatus !== 'all') {
      result = result.filter((order) => order.status === filterStatus);
    }

    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'updatedAt':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'createdAt':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'budget':
          return (b.budget || 0) - (a.budget || 0);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return result;
  }, [orders, filterStatus, sortBy]);

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <Typography variant="h3" color="secondary" className="mb-2">
            {t('noOrders')}
          </Typography>
          <Typography variant="body" color="tertiary">
            {t('createFirst')}
          </Typography>
        </div>
      </div>
    );
  }

  // Check if filtered results are empty
  const hasFilteredResults = filteredAndSortedOrders.length > 0;

  return (
    <div className={className}>
      {/* Sort and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <SortSelect
            options={sortOptions}
            value={sortBy}
            onChange={(value) => setSortBy(value as typeof sortBy)}
          />
        </div>
        <div className="flex-1">
          <SortSelect
            options={filterOptions}
            value={filterStatus}
            onChange={(value) => setFilterStatus(value as typeof filterStatus)}
          />
        </div>
      </div>

      {/* Orders Grid */}
      {hasFilteredResults ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredAndSortedOrders.map((order) => (
            role === 'worker' ? (
              <WorkerOrderCard
                key={order.id}
                order={order}
                onComplete={handleWorkerComplete}
              />
            ) : (
              <OrderCard
                key={order.id}
                order={order}
                proposalsCount={order.proposalsCount}
                unreadProposalsCount={order.unreadProposalsCount}
                onDelete={handleDeleteOrder}
                onCancel={handleCancelOrder}
                onClick={onOrderClick}
                onComplete={handleCompleteOrder}
                onAccept={handleAcceptDirectOrder}
                onRenew={handleRenewOrder}
                onRestore={handleRestoreOrder}
              />
            )
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
          </div>
          <Typography variant="h3" color="secondary" className="mb-2">
            {t('noOrders')}
          </Typography>
          <Typography variant="body" color="tertiary">
            {filterStatus !== 'all' ? 'No orders match the selected filter' : t('createFirst')}
          </Typography>
        </div>
      )}

    </div>
  );
}
